import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { Box, Button, useTheme } from "@mui/material";
import { useSnackbar } from "notistack";
import {
  changeReportSecure,
  changeSeverity,
  getFormDetails,
  changeCompromisedAnonymity,
  changeHostileEnvironment,
  updateRangeDates,
} from "../../../../apis";
import useUnsavedChangesWarning from "../../../../hooks/useUnsavedChangesWarning";
import { formStarter } from "../../../JSONForms/PreviewForm/formStarter";
import { PreviewForm } from "../../../JSONForms/PreviewForm/PreviewForm";
import { Loading } from "../../../Loading/String/Loading";
import { ModalAttachments } from "../../../Modal/Attachments/ModalAttachments";
import {
  OutlineEdit,
  CancelIconSquare,
  TrashCan,
  OutlineUnlock,
} from "../../../Icon";
import { InvestigationSummary } from "./InvestigationSummary";
import ConfirmationModal from "./ConfirmationModal";
import { DeadlineForConclusion } from "./components/DeadlineForConclusion";
import { Severity } from "./components/Severity";
import { Blocked } from "./components/Blocked";
import { AdditionalIndicators } from "./components/AdditionalIndicators";
import { ReportRelated } from "./components/ReportRelated";
import { ComplementsModal } from "./components/ComplementsModal";
import "./Information.scss";

// -----> Tab Report Information <-----//
export const Information = ({
  reportDetail,
  investigationList,
  severityCat,
  improperCat,
  lockedCat,
  id,
  isLoading,
  setIsLoading,
  setShowModalAttachments,
  reportRender,
  showComplementsModal,
  setShowComplementsModal,
  advisorReview,
}) => {
  const [t] = useTranslation("information");
  const theme = useTheme();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  ////////// Editing Column Indicators //////////
  const [editing, setEditing] = useState({
    severity: false,
    locked: false,
    compromised: false,
    hostile: false,
    schedule: false,
  });
  useUnsavedChangesWarning(editing);

  ////////// Loading /////////
  const [loading, setLoading] = useState({
    severity: false,
    locked: false,
    compromised: false,
    hostile: false,
    schedule: false,
  });
  ////////// Switch Value //////////
  const [switches, setSwitches] = useState({});
  ////////// Sections + Add Details //////////
  const [details, setDetails] = useState({
    schedule: reportDetail?.custom_dates?.comment || false,
    severity: reportDetail?.severity?.comment || false,
    locked: reportDetail?.locked?.message || false,
  });

  ////////// General Report Editing //////////
  const [edit, setEdit] = useState(false);
  const [steps, setSteps] = useState(null);
  const [schemaState, setSchemaState] = useState(null);
  const [formData, setFormData] = useState(null);
  const [isValid, setIsValid] = useState(null);
  const [constraints, setConstraints] = useState([]);
  const [isLoadingForm, setIsLoadingForm] = useState(true);
  ////////// Completion Deadline //////////
  // Schedule dates
  const [conclusionDeadline, setConclusionDeadline] = useState(null);
  // Range dates comment
  const [deadLineDetails, setDeadLineDetails] = useState("");
  ////////// Severity Level //////////
  // Severity level options
  const [severityCatTransformed, setSeverityCatTransformed] = useState([]);
  // Severity level selected
  const [severity, setSeverity] = useState("");
  // Comment on selected severity level
  const [severityDetails, setSeverityDetails] = useState("");
  ////////// Report Blocked //////////
  // Report lock options
  const [blockedCategory, setBlockedCategory] = useState([]);
  // Selected lock option
  const [blockSelected, setBlockSelected] = useState("");
  // Locked report observations
  const [blockedDetails, setBlockedDetails] = useState("");
  ////////// Anonymity Compromised //////////
  // Observations on anonymity compromised
  const [textCompromised, setTextCompromised] = useState("");
  ////////// Hostil Enviroment //////////
  // Hostile environment observations
  const [textHostile, setTextHostile] = useState("");
  ////////// Modal Configuration //////////
  // Modal confirm action
  const [modalConfig, setModalConfig] = useState({
    showModal: false,
    confirmAction: () => {},
    modalTitle: "",
    description1: "",
    description2: "",
    modalQuestion: "",
  });
  ////////// For animation purposes //////////
  const [getOut, setGetOut] = useState({
    locked: false,
    compromised: false,
    hostile: false,
  });

  const [complements, setComplements] = useState([]);

  useEffect(() => {
    // if (severityCat?.data) {
    const optionsList = [
      { value: "type::null", label: t("information.none") },
      { value: "type::low", label: t("information.low") },
      { value: "type::medium", label: t("information.medium") },
      { value: "type::hight", label: t("information.hight") },
      { value: "type::other", label: t("information.other") },
    ];
    setSeverityCatTransformed(optionsList);
    // }
    setSeverity(
      reportDetail?.severity?.path !== null &&
        reportDetail?.severity?.path.toLowerCase() !== "type::none"
        ? reportDetail?.severity?.path.toLowerCase()
        : ""
    );

    if (lockedCat?.data) {
      const optionsList = lockedCat.data.map((option) => ({
        value: option.path_locales,
        label: option.label,
      }));
      setBlockedCategory(optionsList);
    }
  }, [improperCat, lockedCat]);

  useEffect(() => {
    if (Array.isArray(reportDetail?.complements)) {
      let newAttachments = [];
      reportDetail.complements.forEach((item) => {
        if (Array.isArray(item.attachments)) {
          newAttachments = [...newAttachments, ...item.attachments];
        }
      });
      setComplements(newAttachments);
    }
  }, [reportDetail?.complements]);

  useEffect(() => {
    setSwitches({
      locked: reportDetail?.locked?.is_locked ?? false,
      compromised:
        reportDetail?.compromised_anonymity?.compromised_anonymity_value ??
        false,
      hostile: reportDetail?.hostile_place?.is_hostile_place_value ?? false,
    });
  }, [reportDetail.id]);

  ////////// Generalization of states, endpoints and other indicator management tools //////////
  const indicatorsMap = {
    // Completion deadline
    schedule: {
      report_identifier: Number(id),
      endPoint: updateRangeDates,
      detailsDB: reportDetail?.custom_dates?.comment ?? "",
      comment: deadLineDetails,
      setComment: setDeadLineDetails,
      setDeadLine: setConclusionDeadline,
      deadLine: conclusionDeadline,
    },
    // Report Severity
    severity: {
      report_id: Number(id),
      endPoint: changeSeverity,
      detailsDB: reportDetail?.severity?.comment ?? "",
      comment: severityDetails,
      setComment: setSeverityDetails,
      setSeverity,
      severity,
    },
    // Report Blocked
    locked: {
      report_id: Number(id),
      endPoint: changeReportSecure,
      detailsDB: reportDetail?.locked?.message ?? "",
      comment: blockedDetails,
      setComment: setBlockedDetails,
      comment_path: blockSelected,
      setComment_path: setBlockSelected,
      confirmed: reportDetail?.locked?.is_locked ?? false,
      value: switches.locked,
      setGetOut,
    },
    // Anonymity Compromised
    compromised: {
      report_identifier: Number(id),
      endPoint: changeCompromisedAnonymity,
      confirmed:
        reportDetail?.compromised_anonymity?.compromised_anonymity_value ??
        false,
      detailsDB: reportDetail?.compromised_anonymity?.comment ?? "",
      comment: textCompromised,
      setComment: setTextCompromised,
      value_to_assign: switches.compromised,
      setGetOut,
    },
    // Hostil Enviroment
    hostile: {
      report_identifier: Number(id),
      endPoint: changeHostileEnvironment,
      confirmed: reportDetail?.hostile_place?.is_hostile_place_value ?? false,
      detailsDB: reportDetail?.hostile_place?.comment,
      comment: textHostile,
      setComment: setTextHostile,
      value_to_assign: switches.hostile,
      setGetOut,
    },
  };

  ////////// Switch onChange event //////////
  const hdlSwitchIndicatorChange = (e) => {
    const status_type = e.target.id;
    const value = e.target.checked;
    setSwitches((prev) => ({ ...prev, [status_type]: value }));
    setEditing((prev) => ({ ...prev, [status_type]: value }));
  };

  ////////// Cancel edit indicator //////////
  const cancelIndicator = (status_type) => {
    const { confirmed, setMessage, setComment_path, setComment } =
      indicatorsMap[status_type];
    // If it has already been confirmed on DB
    if (confirmed) {
      if (setMessage) {
        setMessage("");
      }
      if (status_type === "locked") {
        setComment_path("");
        setComment("");
      }
      // If you are just editing and want to cancel
    } else {
      setSwitches((prev) => ({ ...prev, [status_type]: false }));
      if (status_type === "locked") {
        setComment_path("");
        setComment("");
      }
      if (setMessage) {
        setMessage("");
      }
    }
    setEditing((prevState) => ({
      ...prevState,
      [status_type]: !prevState[status_type],
    }));
  };

  ////////// Saves the information when the switch is turned off //////////
  const turnOffSwitchSave = async (status_type) => {
    const {
      report_id,
      endPoint,
      report_identifier,
      value_to_assign,
      setGetOut,
    } = indicatorsMap[status_type];

    const data = {
      ...(report_id && { report_id }),
      ...(report_identifier && { report_identifier }),
      ...(status_type && { status_type }),
      comment: null,
      comment_path: null,
      ...(value_to_assign && { value_to_assign: false }),
      value: false,
    };
    setLoading((prev) => ({ ...prev, [status_type]: true }));
    const snackLoading = enqueueSnackbar(t("information.sending"), {
      variant: "info",
      persist: true,
      preventDuplicate: true,
    });

    try {
      const resp = await endPoint(data).then((resp) => {
        if (
          resp?.message === "changed" ||
          resp?.success === "custom_dates value was updated successfully"
        ) {
          closeModal();
          reportRender();
          setLoading((prev) => ({ ...prev, [status_type]: false }));

          if (setGetOut) {
            setGetOut((prev) => ({ ...prev, [status_type]: true }));
          }
          setTimeout(() => {
            closeSnackbar(snackLoading);
            reportRender();
            if (setGetOut) {
              setGetOut((prev) => ({ ...prev, [status_type]: false }));
            }
            setSwitches((prev) => ({ ...prev, [status_type]: false }));
            enqueueSnackbar(t("information.successfully_updated"), {
              variant: "success",
            });
          }, 2000);
        }
      });
    } catch (error) {
      setLoading((prev) => ({ ...prev, [status_type]: false }));
      closeSnackbar(snackLoading);
      console.error(error);
    }
  };

  ////////// Save indicator info //////////
  const saveIndicatorInfo = async (status_type) => {
    const {
      report_identifier,
      report_id,
      endPoint,
      deadLine,
      comment,
      setComment,
      value,
      comment_path,
      setComment_path,
    } = indicatorsMap[status_type];

    // Data to endpoint
    const data = {
      ...(report_identifier && {
        report_identifier,
      }),
      ...(report_id && {
        report_id,
      }),
      ...(severity && { path: severity }),
      ...(deadLine && {
        end_day: deadLine.toISOString().split("T")[0],
      }),
      ...(comment && { comment }),
      ...(value && { value }),
      value_to_assign: true,
      ...(comment_path && { comment_path }),
      status_type,
    };

    setLoading((prev) => ({ ...prev, [status_type]: true }));
    const snackLoading = enqueueSnackbar(t("information.sending"), {
      variant: "info",
      persist: true,
      preventDuplicate: true,
    });
    try {
      const resp = await endPoint(data).then((resp) => {
        if (
          resp?.success === "custom_dates value was created successfully" ||
          resp?.success === "custom_dates value was updated successfully" ||
          resp?.severity === "changed" ||
          resp?.message === "changed"
        ) {
          if (setComment) {
            setComment("");
          }
          if (setComment_path) {
            setComment_path("");
          }
          closeSnackbar(snackLoading);
          setEditing((prev) => ({ ...prev, [status_type]: false }));
          setLoading((prev) => ({ ...prev, [status_type]: false }));
          reportRender();
          closeModal();
          enqueueSnackbar(t("information.successfully_updated"), {
            variant: "success",
          });
        }
      });
    } catch (error) {
      setLoading((prev) => ({ ...prev, [status_type]: false }));
      closeSnackbar(snackLoading);
      console.error(error);
    }
  };

  ////////// Edit indicator info //////////
  const editIndicator = (status_type) => {
    const {
      detailsDB,
      setDeadLine,
      setSeverity,
      setComment_path,
      setComment,
      value,
    } = indicatorsMap[status_type];
    // Editing
    if (!editing[status_type]) {
      setComment(detailsDB || "");
      if (status_type === "locked") {
        // Find label in the pre-selected value catalog
        const blockedSelectedLabel = blockedCategory.find(
          (item) => item?.label === reportDetail?.locked?.comment?.label
        );
        setComment_path(blockedSelectedLabel?.value ?? "");
      }
      if (setSeverity) {
        setSeverity(reportDetail?.severity?.path.toLowerCase() || "");
      }
      if (setDeadLine) {
        setDeadLine(dayjs(reportDetail?.custom_dates?.end_day || dateDeadLine));
      }
    } else {
      if (!value) {
        setSwitches((prev) => ({ ...prev, [status_type]: false }));
      }
      // Cancel editing
      setComment("");
      if (setDeadLine) {
        setDeadLine(null);
      }
      if (setComment_path) {
        setComment_path("");
      }
    }
    // Change of status of the selected edition
    setEditing((prevState) => ({
      ...prevState,
      [status_type]: !prevState[status_type],
    }));
  };

  ////////// Modal to confirm action //////////
  const openModalAction = (action, title, desc1, desc2, question) => {
    setModalConfig({
      showModal: true,
      confirmAction: action,
      modalTitle: title,
      description1: desc1,
      description2: desc2,
      modalQuestion: question,
    });
  };
  const closeModal = () => {
    setModalConfig({
      ...modalConfig,
      showModal: false,
      confirmAction: () => {},
      modalTitle: "",
      description1: "",
      description2: "",
      modalQuestion: "",
    });
  };

  ////////// General edit report //////////
  const handleEditChange = (newEditValue) => {
    setEdit(newEditValue);
    if (newEditValue) {
      initializeForm();
    }
  };

  const initializeForm = () => {
    setIsLoadingForm(true);
    getFormDetails(reportDetail?.form_id)
      .then((formLoaded) => {
        formStarter(formLoaded, "answered", reportDetail).then(
          ({
            initSteps,
            initSchemaState,
            initFormData,
            initIsValid,
            initConstraints,
          }) => {
            setSteps(initSteps);
            setSchemaState(initSchemaState);
            setFormData(initFormData);
            setIsValid(initIsValid);
            setConstraints(initConstraints);
            setIsLoadingForm(false);
          }
        );
      })
      .catch(console.error);
  };

  ////////// Dynamic Action buttons //////////
  const actionsBlocking = [
    {
      icon: <OutlineEdit color={theme.palette.primary.main} />,
      name: t("information.edit"),
      onClick: () => editIndicator("locked"),
    },
    {
      icon: <OutlineUnlock color={theme.palette.error.main} />,
      name: t("information.unblock"),
      onClick: () =>
        openModalAction(
          () => {
            turnOffSwitchSave("locked");
          },
          t("information.important"),
          t("information.you_about_unlock_this_report"),
          t("information.please_note_that_this_may_grant_access_other_users"),
          t("information.do_you_want_continue")
        ),
    },
  ];
  const actionsAnonymity = [
    {
      icon: <OutlineEdit color={theme.palette.primary.main} />,
      name: t("information.edit"),
      onClick: () => editIndicator("compromised"),
    },
    {
      icon: <TrashCan color={theme.palette.error.main} />,
      name: t("information.delete"),
      onClick: () =>
        openModalAction(
          () => {
            turnOffSwitchSave("compromised");
          },
          t("information.attention"),
          t("information.you_about_modify_most_recently_data"),
          t("information.this_action_is_irreversible_we_recommend_checking"),
          t("information.do_you_want_continue")
        ),
    },
  ];
  const actionsHostileEnv = [
    {
      icon: <OutlineEdit color={theme.palette.primary.main} />,
      name: t("information.edit"),
      onClick: () => editIndicator("hostile"),
    },
    {
      icon: <TrashCan color={theme.palette.error.main} />,
      name: t("information.delete"),
      onClick: () =>
        openModalAction(
          () => {
            turnOffSwitchSave("hostile");
          },
          t("information.attention"),
          t("information.you_about_modify_most_recently_data"),
          t("information.this_action_is_irreversible_we_recommend_checking"),
          t("information.do_you_want_continue")
        ),
    },
  ];

  ////////// Initial value from date calculation //////////
  const dateDeadLine = new Date(reportDetail?.created_at);
  dateDeadLine.setDate(dateDeadLine.getDate() + reportDetail?.expired_report);

  ////////// Selection severity level //////////
  const selectedSeverityLvl = severityCatTransformed.find(
    (option) => option.value === reportDetail?.severity?.path
  );

  return (
    <>
      {!edit ? (
        <Box
          className="information"
          sx={{
            display: "grid",
            gap: 2.5,
            gridTemplateColumns: { xs: "1fr", md: "8fr 4fr" },
            alignItems: "start",
          }}
        >
          <ConfirmationModal
            show={modalConfig.showModal}
            onHide={closeModal}
            onConfirm={modalConfig.confirmAction}
            title={modalConfig.modalTitle}
            description1={modalConfig.description1}
            description2={modalConfig.description2}
            question={modalConfig.modalQuestion}
          />
          <ModalAttachments
            title={t("information.report_complements")}
            files={complements}
            open={showComplementsModal}
            onClose={() => setShowComplementsModal(false)}
            fileKey="file"
            no_attachments={t("ReportViewer.no_attachments")}
          />
          {/* General Report Information */}
          <Box className="dyTheme1 dyBorder1 rounded" sx={{ mt: 2.5 }}>
            <InvestigationSummary
              reportDetail={reportDetail}
              setShowModalAttachments={setShowModalAttachments}
              advisorReview={advisorReview}
            />
          </Box>

          {/* Additional report parameters */}
          <Box sx={{ p: 0 }}>
            {/* Deadline for conclusion */}
            <DeadlineForConclusion
              dateDeadLine={dateDeadLine}
              deadLineDetails={deadLineDetails}
              setDeadLineDetails={setDeadLineDetails}
              conclusionDeadline={conclusionDeadline}
              setConclusionDeadline={setConclusionDeadline}
              reportDetail={reportDetail}
              editing={editing}
              loading={loading}
              details={details}
              setDetails={setDetails}
              editIndicator={editIndicator}
              saveIndicatorInfo={saveIndicatorInfo}
              openModalAction={openModalAction}
            />
            {/* Severity */}
            <Severity
              selectedSeverityLvl={selectedSeverityLvl}
              severityCatTransformed={severityCatTransformed}
              severity={severity}
              setSeverity={setSeverity}
              severityDetails={severityDetails}
              setSeverityDetails={setSeverityDetails}
              reportDetail={reportDetail}
              editing={editing}
              loading={loading}
              details={details}
              setDetails={setDetails}
              editIndicator={editIndicator}
              saveIndicatorInfo={saveIndicatorInfo}
              openModalAction={openModalAction}
            />
            {/* Blocked */}
            <Blocked
              actionsBlocking={actionsBlocking}
              blockedCategory={blockedCategory}
              switches={switches}
              getOut={getOut}
              cancelIndicator={cancelIndicator}
              hdlSwitchIndicatorChange={hdlSwitchIndicatorChange}
              blockedDetails={blockedDetails}
              setBlockedDetails={setBlockedDetails}
              blockSelected={blockSelected}
              setBlockSelected={setBlockSelected}
              reportDetail={reportDetail}
              editing={editing}
              loading={loading}
              details={details}
              setDetails={setDetails}
              saveIndicatorInfo={saveIndicatorInfo}
            />
            {/* Report aditional indicators */}
            <AdditionalIndicators
              actionsAnonymity={actionsAnonymity}
              actionsHostileEnv={actionsHostileEnv}
              switches={switches}
              getOut={getOut}
              cancelIndicator={cancelIndicator}
              hdlSwitchIndicatorChange={hdlSwitchIndicatorChange}
              textCompromised={textCompromised}
              setTextCompromised={setTextCompromised}
              textHostile={textHostile}
              setTextHostile={setTextHostile}
              reportDetail={reportDetail}
              editing={editing}
              loading={loading}
              saveIndicatorInfo={saveIndicatorInfo}
            />
            {/* Related Reports */}
            <ReportRelated
              relatedList={reportDetail?.related}
              investigationList={investigationList}
              reportRender={reportRender}
              id={id}
              openModalAction={openModalAction}
              closeModal={closeModal}
            />
          </Box>
        </Box>
      ) : (
        <Box sx={{ p: 0, width: "100%" }}>
          <Box sx={{ mt: 2.5 }}>
            <Box sx={{ mt: 2.5, display: "flex", justifyContent: "flex-end" }}>
              <Button
                onClick={() => handleEditChange(edit ? false : true)}
                disableElevation
                sx={{
                  mx: 2,
                  px: 1,
                  py: 0.5,
                  color: "#909ca4",
                  backgroundColor: "#d3dbe1",
                  backgroundImage:
                    "linear-gradient(to bottom, #d3dbe1, #d9d9d9 120%)",
                  border: "1px solid #bdcad0",
                  borderRadius: 1,
                  textTransform: "none",
                  boxShadow: "none",
                  "& svg g": { fill: "#909ca4" },
                  "&:hover": { backgroundColor: "#c4cdd3" },
                }}
                startIcon={<CancelIconSquare className="icons" />}
              >
                <Box
                  component="span"
                  sx={{ display: { xs: "none", md: "inline-block" } }}
                >
                  {t("information.cancel_edit")}
                </Box>
              </Button>
            </Box>
            {/* Edicion del formulario */}
            {isLoadingForm ? (
              <Loading />
            ) : (
              <PreviewForm
                steps={steps}
                schemaState={schemaState}
                setSchemaState={setSchemaState}
                formData={formData}
                setFormData={setFormData}
                isValid={isValid}
                setIsValid={setIsValid}
                constraints={constraints}
                setConstraints={setConstraints}
                showButtons={true}
                stepClick={false}
                answered={true}
                id={reportDetail?.id}
                edit={edit}
              />
            )}
          </Box>
        </Box>
      )}
    </>
  );
};